import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    const pollPaymentStatus = async (attempt = 0) => {
      if (cancelled) return;
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        const data = await response.json();
        if (cancelled) return;
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        }

        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        timeoutId = setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (cancelled) return;
        timeoutId = setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
      }
    };

    pollPaymentStatus(0);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price / 100);
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center py-12">
      <div className="container mx-auto px-4 max-w-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="card-shadow">
            <CardContent className="p-8 text-center">
              {status === 'loading' && (
                <>
                  <Loader2 className="w-16 h-16 mx-auto mb-6 text-[hsl(var(--primary))] animate-spin" />
                  <h1 className="text-2xl font-semibold mb-2">{t('payment.processing')}</h1>
                  <p className="text-[hsl(var(--muted-foreground))]">
                    Bitte warten Sie, während wir Ihre Zahlung überprüfen...
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-[hsl(var(--chart-2)/0.1)] rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-[hsl(var(--chart-2))]" />
                  </div>
                  <h1 className="text-2xl font-semibold mb-2 text-[hsl(var(--chart-2))]">
                    {t('payment.success.title')}
                  </h1>
                  <p className="text-[hsl(var(--muted-foreground))] mb-6">
                    {t('payment.success.message')}
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                    {t('payment.success.details')}
                  </p>
                  {paymentData && (
                    <div className="bg-[hsl(var(--muted))] rounded-lg p-4 mb-6 text-left">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-[hsl(var(--muted-foreground))]">Betrag</span>
                        <span className="font-medium">CHF {formatPrice(paymentData.amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[hsl(var(--muted-foreground))]">Status</span>
                        <span className="font-medium text-[hsl(var(--chart-2))]">Bezahlt</span>
                      </div>
                    </div>
                  )}
                  <Link to="/">
                    <Button className="w-full" data-testid="payment-success-home-button">
                      {t('payment.success.backHome')}
                    </Button>
                  </Link>
                </>
              )}

              {(status === 'timeout' || status === 'error' || status === 'expired') && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-[hsl(var(--chart-3)/0.1)] rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-[hsl(var(--chart-3))]" />
                  </div>
                  <h1 className="text-2xl font-semibold mb-2">
                    Zahlung wird verarbeitet
                  </h1>
                  <p className="text-[hsl(var(--muted-foreground))] mb-6">
                    Ihre Zahlung wird noch verarbeitet. Sie erhalten eine Bestätigung per E-Mail.
                  </p>
                  <Link to="/">
                    <Button className="w-full">
                      {t('payment.success.backHome')}
                    </Button>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
